import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Search, Loader2, FileText } from 'lucide-react';
import { StoriesAPI } from '../api';

const SearchModal = ({ open, onClose }) => {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const abortRef = useRef(null);

  // Focus + reset on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults([]);
      setSelected(0);
    }
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Debounced search
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const res = await StoriesAPI.search(q.trim(), 12);
      if (!ctrl.signal.aborted) {
        setResults(res?.results || []);
        setSelected(0);
      }
    } catch (err) {
      if (!ctrl.signal.aborted) setResults([]);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 300);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[selected];
      if (pick) {
        navigate(`/article/${pick.slug}`);
        onClose();
      }
    }
  };

  const highlight = (text, q) => {
    if (!q || !text) return text;
    try {
      const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
      const parts = text.split(re);
      return parts.map((p, i) =>
        re.test(p) ? <mark key={i} className="bg-[#c9a961]/40 text-[#1a1414] px-0.5">{p}</mark> : p
      );
    } catch (e) { return text; }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-[#faf7f2]/98 backdrop-blur-sm flex flex-col items-center pt-20 md:pt-28 px-4 md:px-6 overflow-y-auto">
      <button
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-6 right-6 text-[#1a1414] hover:text-[#8b2c3f] transition-colors"
      >
        <X size={26} />
      </button>

      <div className="w-full max-w-3xl">
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#7a716c] mb-4 text-center">
          Tapez au moins <span className="text-[#c9a961] font-semibold">2 caractères</span> —{' '}
          <span className="hidden md:inline">Utilisez ↑ ↓ pour naviguer, </span>
          <span className="text-[#c9a961] font-semibold">Entrée</span> pour ouvrir,{' '}
          <span className="text-[#c9a961] font-semibold">Échap</span> pour fermer.
        </p>

        <div className="flex items-center border-b-2 border-[#1a1414] pb-3">
          {loading ? (
            <Loader2 size={24} className="text-[#8b2c3f] mr-3 animate-spin" />
          ) : (
            <Search size={24} className="text-[#8b2c3f] mr-3" />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            type="text"
            placeholder="Rechercher une histoire, un mot-clé…"
            className="flex-1 bg-transparent outline-none text-2xl md:text-3xl italic text-[#1a1414] placeholder-[#c9c0b3]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          />
        </div>

        {/* Résultats */}
        <div className="mt-6 pb-16">
          {query.trim().length < 2 && (
            <p className="text-center text-[#7a716c] italic mt-8"
               style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Commencez à taper pour découvrir des histoires…
            </p>
          )}

          {query.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="text-center mt-10">
              <p className="text-[#7a716c] italic mb-2"
                 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                Aucune histoire ne correspond à « <span className="text-[#8b2c3f]">{query}</span> ».
              </p>
              <p className="text-[12px] text-[#7a716c]">Essayez avec d&apos;autres mots-clés.</p>
            </div>
          )}

          {results.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#c9a961] font-bold mb-4">
                {results.length} résultat{results.length > 1 ? 's' : ''}
              </div>
              <ul className="space-y-2">
                {results.map((r, idx) => (
                  <li key={r.id}>
                    <Link
                      to={`/article/${r.slug}`}
                      onClick={onClose}
                      className={`group grid grid-cols-[70px_1fr] gap-4 items-start p-3 border transition-colors ${
                        idx === selected
                          ? 'border-[#8b2c3f] bg-white'
                          : 'border-transparent hover:border-[#e6ddd0] hover:bg-white'
                      }`}
                    >
                      <img
                        src={r.image}
                        alt=""
                        className="w-full aspect-square object-cover bg-[#efe8de]"
                        loading="lazy"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] mb-1">
                          <span className="text-[#8b2c3f] font-bold">{r.category}</span>
                          <span className="text-[#c9a961]">•</span>
                          <span className="text-[#7a716c] normal-case tracking-normal">{r.date}</span>
                        </div>
                        <h3
                          className="text-[16px] md:text-[18px] font-bold text-[#1a1414] leading-snug group-hover:text-[#8b2c3f] transition-colors line-clamp-2"
                          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                          {highlight(r.title, query.trim())}
                        </h3>
                        <p className="text-[13px] text-[#7a716c] mt-1 line-clamp-2">
                          {highlight(r.excerpt, query.trim())}
                        </p>
                        <div className="mt-2 text-[11px] text-[#c9a961] inline-flex items-center gap-1">
                          <FileText size={11} /> Lire l&apos;article
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
